import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Input,
  TextField,
  Button,
} from "@material-ui/core";
import { FieldArray, useFormikContext } from "formik";
import AddIcon from "@material-ui/icons/AddCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";

import { CaseDataResponse, Task } from "../../../types/CaseTypes";
import { useCaseViewStyle } from "../../../components/CaseView/useCaseViewStyle";
import { StartIconButton } from "../../shared/helpers/GlobalStylesv5";
import { Typography } from "@mui/material";
import { tokens } from "../../../theme/tokens";

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
}

function TaskDialog({ open, onClose }: TaskDialogProps) {
  const classes = useCaseViewStyle();
  const { values, setFieldValue, submitForm } =
    useFormikContext<CaseDataResponse>();

  const [editingTask, setEditingTask] = useState<Task>({
    id: null,
    title: "",
    desc: "",
    status: false,
  });
  const [isEditing, setIsEditing] = useState(false);

  const tasks = values.case_detail.case_details.tasks || [];

  const handleClose = () => {
    submitForm();
    resetTask();
    setIsEditing(false);
    onClose();
  };

  const resetTask = () => {
    setEditingTask({ id: null, title: "", desc: "", status: false });
  };

  const handleEditTask = (index: number) => {
    const task = tasks[index];
    setEditingTask({
      id: index,
      title: task.title,
      desc: task.desc,
      status: task.status,
    });
    setIsEditing(true);
  };

  const handleAddNewTask = () => {
    resetTask();
    setIsEditing(true);
  };

  const toggleTaskStatus = (index: number) => {
    const currentStatus = tasks[index].status;
    setFieldValue(
      `case_detail.case_details.tasks[${index}].status`,
      !currentStatus,
      false,
    );
  };

  const updateTaskField = (field: "title" | "desc", value: string) => {
    if (editingTask.id === null) {
      setEditingTask({ ...editingTask, [field]: value });
    } else {
      setFieldValue(
        `case_detail.case_details.tasks[${editingTask.id}].${field}`,
        value,
        false,
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      BackdropProps={{ style: { position: "absolute" } }}
      container={document.getElementById("cases-parent-div")}
      classes={{ paper: classes.tasksPaper }}
    >
      <Typography variant="h5" className={classes.taskDialogHeader}>
        Tasks
      </Typography>
      <DialogContent>
        <div style={{ display: "flex", columnGap: "25px" }}>
          {/* Task List */}
          <List className={classes.tasksList}>
            <ListItem
              disableGutters
              divider
              style={{ marginTop: "2px", cursor: "pointer" }}
              onClick={handleAddNewTask}
            >
              {/* <ListItemIcon>
                <AddIcon style={{ fill: tokens.color.brand.primary, fontSize: "1.8rem" }} />
              </ListItemIcon>
              <ListItemText
                primary="Add a task"
                className={classes.taskListText}
              /> */}
              <StartIconButton onClick={handleAddNewTask} icon={<AddIcon />}>
                Add a new task
              </StartIconButton>
            </ListItem>

            {tasks.map((task, index) => (
              <ListItem key={index} disableGutters divider>
                <ListItemIcon>
                  {task.status ? (
                    <CheckCircleOutlineIcon
                      className={classes.taskIcon}
                      onClick={() => toggleTaskStatus(index)}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      className={classes.taskIcon}
                      onClick={() => toggleTaskStatus(index)}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={task.title}
                  className={classes.taskListText}
                  // style={{ color: "#004b5d" }}
                />
                <ListItemIcon>
                  <span className="fa-stack">
                    <i
                      className={`fa-regular fa-circle fa-stack-2x ${classes.stack}`}
                      // style={{ color: "#0097bb" }}
                    />
                    <i
                      className={`fa-solid fa-pen fa-stack ${classes.icon} ${classes.iconHover}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleEditTask(index)}
                    />
                  </span>
                </ListItemIcon>
                <ListItemIcon style={{ minWidth: "0px" }}>
                  <FieldArray name="case_detail.case_details.tasks">
                    {({ remove }) => (
                      <span className="fa-stack">
                        <i
                          className={`fa-regular fa-circle fa-stack-2x ${classes.stack}`}
                          style={{ color: tokens.color.brand.primaryAction }}
                        />
                        <i
                          className={`fa-solid fa-trash fa-stack ${classes.icon} ${classes.iconHover}`}
                          style={{ color: tokens.color.brand.primaryAction, cursor: "pointer" }}
                          onClick={() => {
                            remove(index);
                            setIsEditing(false);
                          }}
                        />
                      </span>
                    )}
                  </FieldArray>
                </ListItemIcon>
              </ListItem>
            ))}
          </List>

          {/* Task Editor */}
          {isEditing && (
            <div className={classes.addTasks}>
              <List>
                <ListItem disableGutters divider>
                  <Input
                    className={classes.tasksInput}
                    placeholder="Title"
                    value={
                      editingTask.id === null
                        ? editingTask.title
                        : tasks[editingTask.id]?.title
                    }
                    onChange={(e) => updateTaskField("title", e.target.value)}
                    disableUnderline
                    required
                  />
                </ListItem>
                <ListItem disableGutters>
                  <TextField
                    fullWidth
                    multiline
                    value={
                      editingTask.id === null
                        ? editingTask.desc
                        : tasks[editingTask.id]?.desc
                    }
                    onChange={(e) => updateTaskField("desc", e.target.value)}
                    required
                    rows={5}
                    minRows={5}
                    placeholder="Add a description"
                  />
                </ListItem>
                {editingTask.id === null && (
                  <FieldArray name="case_detail.case_details.tasks">
                    {({ push }) => (
                      <Button
                        className={classes.postButton}
                        disabled={!editingTask.title || !editingTask.desc}
                        onClick={() => {
                          push({
                            title: editingTask.title,
                            desc: editingTask.desc,
                            status: editingTask.status,
                          });
                          resetTask();
                        }}
                      >
                        Add task
                      </Button>
                    )}
                  </FieldArray>
                )}
              </List>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TaskDialog;
